import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButtons, IonMenuButton,
  IonItem, IonInput, IonTextarea, IonChip, IonButton, IonToggle, IonDatetime,
  IonSelect, IonSelectOption, IonIcon, IonSpinner, IonNote,
} from '@ionic/react';
import { sparklesOutline } from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { PLATFORM_IDS, PLATFORMS, type PlatformId } from '@ai-media/shared';
import { api, apiErrorMessage } from '@/lib/api';
import { asString } from '@/lib/collections';
import { toast } from '@/store/ui.store';
import MediaPicker from '@/components/MediaPicker';

export default function ComposerPage() {
  const { t } = useTranslation();
  const history = useHistory();

  const [title, setTitle] = useState('');
  const [draft, setDraft] = useState('');
  const [selected, setSelected] = useState<Set<PlatformId>>(new Set());
  const [hashtags, setHashtags] = useState('');
  const [tone, setTone] = useState('friendly');
  const [length, setLength] = useState('medium');
  const [aiEnabled, setAiEnabled] = useState(true);
  /** Media library assets picked for this post — resolved to URLs on the API. */
  const [mediaAssetIds, setMediaAssetIds] = useState<string[]>([]);
  const [publishAt, setPublishAt] = useState<string | null>(null);
  const [requireApproval, setRequireApproval] = useState(false);
  const [busy, setBusy] = useState<'' | 'ai' | 'save'>('');

  const togglePlatform = (id: PlatformId) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const generateAi = async () => {
    if (!draft.trim()) {
      toast(t('assistant.needTopic'), 'warning');
      return;
    }
    setBusy('ai');
    try {
      const { data } = await api.post<{ text: string }>('/ai/generate', {
        prompt: draft,
        tone,
        length,
        hashtags: hashtags.split(/[\s,]+/).filter(Boolean),
        platforms: selected.size ? [...selected] : [...PLATFORM_IDS],
      });
      setDraft(asString(data?.text));
      toast('Draft generated', 'success');
    } catch (error) {
      toast(apiErrorMessage(error), 'danger');
    } finally {
      setBusy('');
    }
  };

  const submit = async () => {
    if (!draft.trim() || selected.size === 0) {
      toast(t('validation.required'), 'warning');
      return;
    }
    setBusy('save');
    try {
      await api.post('/content/posts', {
        title: title.trim() || null,
        draft,
        platformIds: [...selected],
        mediaAssetIds: mediaAssetIds.length > 0 ? mediaAssetIds : undefined,
        aiOptions: aiEnabled ? { tone, length } : undefined,
        publishAt: publishAt ? new Date(publishAt).toISOString() : null,
        requireApproval,
      });
      toast(t('common.success'), 'success');
      history.push('/app/posts');
    } catch (error) {
      toast(apiErrorMessage(error), 'danger');
    } finally {
      setBusy('');
    }
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>{t('content.newPost')}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div className="page-shell page-shell--narrow">
          <IonItem lines="none">
            <IonInput label={t('content.postTitle')} labelPlacement="stacked" value={title}
              onIonInput={(event) => setTitle((event.target as HTMLIonInputElement).value as string)} />
          </IonItem>
          <IonItem lines="none">
            <IonTextarea label={`${t('content.draft')} *`} labelPlacement="stacked" rows={8} value={draft}
              onIonInput={(event) => setDraft((event.target as HTMLIonTextareaElement).value as string)} />
          </IonItem>
          <div style={{ display: 'flex', gap: 8, margin: '6px 2px' }}>
            <IonButton size="small" disabled={busy !== ''} onClick={() => void generateAi()}>
              {busy === 'ai' ? <IonSpinner name="dots" /> : <IonIcon slot="start" icon={sparklesOutline} />}
              {t('content.aiDraft')}
            </IonButton>
          </div>

          <h6 className="section-title">{t('content.platforms')} *</h6>
          <div className="composer-platforms">
            {PLATFORM_IDS.map((id) => (
              <IonChip key={id} color={selected.has(id) ? 'primary' : 'medium'} outline={!selected.has(id)}
                onClick={() => togglePlatform(id)}>
                <IonNote style={{ color: PLATFORMS[id].color, fontWeight: 700 }}>{PLATFORMS[id].label}</IonNote>
              </IonChip>
            ))}
          </div>

          <IonItem lines="none">
            <IonInput label={t('content.hashtags')} labelPlacement="stacked" placeholder="#growth #launch" value={hashtags}
              onIonInput={(event) => setHashtags((event.target as HTMLIonInputElement).value as string)} />
          </IonItem>
          <h6 className="section-title">{t('media.title')}</h6>
          <MediaPicker selectedIds={mediaAssetIds} onChange={setMediaAssetIds} platforms={[...selected]} />
          <IonItem lines="none">
            <IonSelect label={t('content.tone')} interface="popover" value={tone}
              onIonChange={(event) => setTone(event.detail.value as string)}>
              {['friendly', 'professional', 'witty', 'inspirational', 'bold', 'educational'].map((option) => (
                <IonSelectOption key={option} value={option}>{option}</IonSelectOption>
              ))}
            </IonSelect>
          </IonItem>
          <IonItem lines="none">
            <IonSelect label={t('content.length')} interface="popover" value={length}
              onIonChange={(event) => setLength(event.detail.value as string)}>
              {['short', 'medium', 'long'].map((option) => (
                <IonSelectOption key={option} value={option}>{option}</IonSelectOption>
              ))}
            </IonSelect>
          </IonItem>
          <IonItem lines="none">
            <IonToggle checked={aiEnabled} onIonChange={(event) => setAiEnabled(event.detail.checked)}>
              {t('content.aiDraft')}
            </IonToggle>
          </IonItem>

          <h6 className="section-title">{t('content.scheduleFor')}</h6>
          <IonDatetime className="composer-datetime" presentation="date-time" min={new Date().toISOString()} value={publishAt ?? undefined}
            onIonChange={(event) => setPublishAt((event.detail.value as string) ?? null)} />
          <IonItem lines="none">
            <IonToggle checked={requireApproval} onIonChange={(event) => setRequireApproval(event.detail.checked)}>
              {t('content.requireApproval')}
            </IonToggle>
          </IonItem>

          <IonButton expand="block" className="ion-margin-top" disabled={busy !== ''} onClick={() => void submit()}>
            {busy === 'save' ? <IonSpinner name="dots" /> : publishAt ? t('content.schedule') : t('content.immediatePublish')}
          </IonButton>
        </div>
      </IonContent>
    </IonPage>
  );
}
